import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import OTPInput from 'react-otp-input';

import API from 'src/apis';
import { analytics, trackEvent } from 'src/analytics';
import { EVENTS, SCREENS } from 'src/analytics/events';
import { DEFAULT_MESSAGES, GOOGLE_FORMS } from 'src/common/constants';
import { AppType, fetchTatvaAiToken, setAppType } from 'src/helpers/rest';
import BrandLoading from 'src/components/atoms/brand_loading';
import ContactInput from 'src/components/atoms/contact_input';

import LandingLoader from 'src/assets/images/landing_page.gif';
import TatvaCareLogo from 'src/assets/images/tatvacare_logo.png';
import PhoneIcon from 'src/assets/images/phone.svg';
import SuccessIcon from 'src/assets/images/success.svg';

import styles from './index.module.scss';

type ScreenType = 'signin' | 'otp' | 'thanks' | 'request_access';

const LoginPage: React.FunctionComponent = () => {

    const [searchParams] = useSearchParams();

    const otpButtonRef = useRef<HTMLButtonElement>(null);

    const [screen, setScreen] = useState<ScreenType>('signin');
    const [width, setWidth] = useState<number>(window.innerWidth);

    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [otp, setOtp] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [otpResp, setOtpResp] = useState<any>();
    const [otpTimer, setOtpTimer] = useState(60);
    const [tokenLoading, setTokenLoading] = useState<boolean>(true);
    const [appType, _setAppType] = useState<AppType>('tatva_ai');

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let interval: string | number | NodeJS.Timer | undefined;
        if (otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer(otpTimer - 1);
            }, 1000);
        } else {
            clearInterval(interval);
        }

        return () => clearInterval(interval);
    }, [otpTimer]);

    useEffect(() => {
        if (otp.length === 6) {
            const container = otpButtonRef.current;
            if (container) {
                container.focus();
            }
        }
    }, [otp]);

    useEffect(() => {
        const authToken = searchParams.get('authToken');
        if (!authToken) {
            setTokenLoading(false);
        } else {
            localStorage.clear();
            setAppType(searchParams, _setAppType);
            const url = new URL(window.location.href);
            url.searchParams.delete('authToken');
            window.history.replaceState(null, '', url.toString());
            fetchTatvaAiToken(authToken, setTokenLoading, false)
                .then((data) => {
                    window.history.replaceState(null, '', data?.welcome_visited ? '/' : '/welcome');
                    window.location.reload();
                });
        }
    }, [searchParams]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const regex = /^[0-9]{0,10}$/;

        if (regex.test(value)) {
            setPhoneNumber(value);
            setError('');
        }
    };

    const onPressSendOTP = async () => {
        if (!phoneNumber || phoneNumber.length !== 10) return;
        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_CONTINUE_MOBILE, {
            mobile_number: phoneNumber,
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });
        error && setError('');
        try {
            setLoading(true);
            const resp = await API.SendOTP({ phone_number: '91' + phoneNumber });
            if (resp.status && resp.status === 'success') {
                setScreen('otp');
            } else {
                setError('Account not found. Please try again!');
                throw new Error(resp.msg || 'Something went wrong while sending OTP!');
            }
        } catch (error) {
            setError('Error while send otp. Please try again!');
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const onPressVerifyOTP = async () => {
        if (!phoneNumber || !otp || otp.length !== 6) return;
        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_CONTINUE_OTP, {
            otp: otp,
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });
        error && setError('');
        try {
            setLoading(true);
            const resp = await API.VerifyOTP({ mobile_number: '91' + phoneNumber, otp: otp });
            if (resp.status === 'success') {
                if (resp.subscribed === 1) {
                    if (resp?.data?.token) {
                        doLogin(resp.data);
                        return;
                    } else {
                        throw new Error(DEFAULT_MESSAGES.INTERNAL_SERVER_ERROR);
                    }
                } else {
                    setOtpResp(resp?.data);
                    trackEvent(EVENTS.AUTH_SCREEN.OTP_VERIFIED, {
                        otp: otp,
                        screen_name: SCREENS.LOGIN_PAGE,
                        utm: true,
                        search: window.location.search
                    });
                }
            } else {
                if (resp.data.message === 'Invalid OTP') {
                    setError('Invalid OTP!');
                    setLoading(false);
                    trackEvent(EVENTS.AUTH_SCREEN.OTP_NOT_VERIFIED, {
                        error_message: 'Invalid OTP!',
                        error_id: '',
                        screen_name: SCREENS.LOGIN_PAGE,
                        utm: true,
                        search: window.location.search
                    });
                    return;
                }
            }
            setScreen('request_access');
            setLoading(false);
        } catch (error) {
            setError(DEFAULT_MESSAGES.INTERNAL_SERVER_ERROR);
            setLoading(false);
            console.log(error);
        }
    };

    const doLogin = (data: any) => {
        const { token, user_id, first_name = '', last_name = '', mobile, email, db_id } = data;
        localStorage.setItem('token', token);
        analytics.setUserProperties({ user_id, first_name, last_name, mobile, email, db_id });

        setTimeout(() => {
            trackEvent(EVENTS.AUTH_SCREEN.OTP_VERIFIED, {
                otp: otp,
                screen_name: SCREENS.LOGIN_PAGE,
                utm: true,
                search: window.location.search
            });
            window.location.reload();
        }, 500);
    };

    const onPressAccessRequest = async () => {
        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_REQUEST_ACCESS, {
            mobile_number: phoneNumber,
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });

        if (!otpResp?.user_id) {
            window.open(GOOGLE_FORMS.SUBMIT_INTREST, '_blank');
            setScreen('thanks');
            return;
        }
        try {
            setLoading(true);
            const formdata = new FormData();

            formdata.append('user_id', otpResp.user_id);
            formdata.append('db_id', otpResp.db_id || '-');
            formdata.append('first_name', otpResp.first_name || '-');
            formdata.append('last_name', otpResp.last_name || '-');
            formdata.append('mobile', otpResp.mobile || '-');
            formdata.append('email', otpResp.email || '-');

            const requestOptions = {
                method: 'POST',
                body: formdata,
            };

            await fetch(GOOGLE_FORMS.CAPTURE_INTREST, requestOptions);
        } catch (error) {
            console.log(error);
        } finally {
            setScreen('thanks');
            setLoading(false);
        }
    };

    const onPressBackToLogin = () => {
        setLoading(false);
        setPhoneNumber('');
        setOtp('');
        setOtpResp(null);
        setError('');
        setScreen('signin');

        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_BACK_TO_LOGIN, {
            mobile_number: phoneNumber,
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });
    };

    const onPressResendOTP = async () => {
        try {
            trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_RESEND_OTP, {
                screen_name: SCREENS.LOGIN_PAGE,
                utm: true,
                search: window.location.search
            });
            setLoading(true);
            const resendOtp = await API.ResendOTP({ phone_number: '91'.concat(phoneNumber) });
            if (resendOtp.status === 'success') {
                // Handle success.
                setOtpTimer(60);
            } else if (resendOtp.status === 'error' && resendOtp?.data?.otp_response?.message) {
                setError(resendOtp?.data?.otp_response?.message || DEFAULT_MESSAGES.INTERNAL_SERVER_ERROR);
                setLoading(false);
                return;
            } else {
                throw new Error(DEFAULT_MESSAGES.INTERNAL_SERVER_ERROR);
            }
        } catch (error) {
            console.log(error);
            setError(DEFAULT_MESSAGES.INTERNAL_SERVER_ERROR);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, functionToBeCalled: Function) => {
        if (event.key === 'Enter') {
            functionToBeCalled();
        }
    };

    const onPressTAC = () => {
        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_TNS, {
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });
    };

    const onPressPP = () => {
        trackEvent(EVENTS.AUTH_SCREEN.TAP_ON_VIEW_PP, {
            screen_name: SCREENS.LOGIN_PAGE,
            utm: true,
            search: window.location.search
        });
    };

    return (<>
        {tokenLoading
            ? <BrandLoading app={appType} />
            : <div className={styles.loginpage_wrapper}>
                {width < 900 &&
                    <div className={styles.mobile_loginpage_container}>
                        <div className={styles.mobile_loginpage_upper}>
                            <div className={styles.mobile_loginpage_container}>
                                <img src={LandingLoader} alt='gif' className={styles.mobile_landing_loader} />
                                <span className={styles.mobile_loginpage_main_heading}>Welcome to Tatva Ai</span>
                                <span className={styles.mobile_loginpage_main_desc}>Revolutionize Your Research with Every Query</span>

                                <div className={styles.mobile_loginpage_desc_container}>
                                    <span className={styles.mobile_loginpage_desc}>Powered by</span>
                                    <img height={'19px'} src={TatvaCareLogo} alt={'tatva_care_logo'} />
                                </div>
                            </div>
                        </div>

                        {screen === 'request_access'
                            && <>
                                <span className={styles.opps_heading_mobile}>
                                    Oops! It looks like you haven't subscribed to Tavta AI yet.
                                </span>
                                <span className={styles.opps_des_mobile}>
                                    Please click the 'Request Access' button to let us know you're interested.
                                </span>
                                <button
                                    className={`${styles.request_access_button_mobile} ${loading ? styles.disabled : ''}`}
                                    onClick={() => onPressAccessRequest()}
                                >
                                    {loading
                                        ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                        : 'Request Access'}
                                </button>
                            </>}

                        {screen === 'thanks'
                            && <><img src={SuccessIcon} alt='gif' style={{ height: '60px', marginTop: '15px' }} />
                                <span className={styles.thanks_heading_mobile}>
                                    Thank you for submitting your interest!
                                </span>
                                <span className={styles.thanks_des_mobile}>
                                    Gear up, Doctor! Your gateway to groundbreaking research awaits—Tatva-ai credentials landing in your inbox within 48 hours!
                                </span>

                                <button
                                    className={`${styles.request_access_button_mobile} ${styles.back_to_login} ${loading ? styles.disabled : ''}`}
                                    onClick={onPressBackToLogin}
                                >
                                    Back to Login
                                </button>
                            </>}

                        {screen === 'signin' && <>
                            <div className={styles.mobile_loginpage_lower}>
                                <span className={styles.mobile_login_phone_label}>Enter the mobile number</span>
                                {/* <div className={styles.phone_icon_wrapper}>
                                    <img src={PhoneIcon} alt='phone_icon' className={styles.phone_icon} />
                                    <span className={styles.mobile_login_phone_country_code}>+91</span>
                                </div>
                                <input
                                    className={styles.mobile_login_phone_input_field}
                                    type='number'
                                    inputMode='numeric'
                                    value={phoneNumber}
                                    onChange={handleChange}
                                /> */}
                                <ContactInput
                                    value={phoneNumber}
                                    mainContainerClass={styles.main_login_phone_input}
                                    inputClass={styles.mobile_login_phone_input_field}
                                    isPhoneIcon={true}
                                    onChange={handleChange}
                                    onKeyDown={(event) => handleKeyDown(event, onPressSendOTP)}
                                    placeholder='Phone...'
                                />
                            </div>
                            <span className={styles.terms_and_condition}>
                                By signing up, you agree to Tatva Care
                                <span className={styles.privacy_text}>
                                    <span onClick={onPressTAC}>Terms & Service</span>
                                    <span onClick={onPressPP}>View privacy policy</span>
                                </span>
                            </span>
                            <button
                                className={`${styles.continue_button} ${(phoneNumber.length !== 10 || loading) ? styles.disabled : ''}`}
                                disabled={(phoneNumber.length !== 10 || loading)}
                                onClick={onPressSendOTP}
                            >
                                {loading
                                    ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                    : 'Continue'}
                            </button>
                        </>}
                        {screen === 'otp'
                            && <div className={styles.mobile_otp_input_wrapper}>
                                <span className={styles.mobile_otp_input_label}>Enter the OTP</span>
                                <OTPInput
                                    value={otp}
                                    onChange={setOtp}
                                    numInputs={6}
                                    inputStyle={styles.mobile_otp_input}
                                    renderInput={(props) => <input {...props} />}
                                    containerStyle={styles.mobile_otp_input_main}
                                    inputType='number'
                                />
                                {error && <p className={styles.error_message}>{error}</p>}
                                {otpTimer > 0
                                    ? <span className={styles.mobile_resend_otp + ' ' + styles.disabled_otp}>Resend OTP ({formatTime(otpTimer)})</span>
                                    : <span className={styles.mobile_resend_otp} onClick={onPressResendOTP}>Resend OTP</span>}
                                <button
                                    className={`${styles.mobile_continue_button} ${(otp.length !== 6 || loading) ? styles.disabled : ''}`}
                                    disabled={(otp.length !== 6 || loading)}
                                    onClick={onPressVerifyOTP}
                                >
                                    {loading
                                        ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                        : 'Continue'}
                                </button>
                            </div>
                        }
                    </div>}
                {width >= 900 &&
                    <div className={styles.loginpage_content}>
                        <div className={styles.loginpage_content_left}>
                            <div className={styles.loginpage_content_left_container}>
                                <img src={LandingLoader} alt='gif' className={styles.landing_loader} />
                                <span className={styles.login_page_main_heading}>Welcome to Tatva Ai</span>
                                <span className={styles.login_page_main_desc}>Revolutionize Your Research with Every Query</span>
                                <div className={styles.loginpage_desc_container}>
                                    <span className={styles.login_page_desc}>Powered by</span>
                                    <img height={'24px'} src={TatvaCareLogo} alt={'tatva_care_logo'} />
                                </div>
                                {/* <div className={styles.display_text}>
                            Tatva-ai exclusively offers peer-reviewed, medically relevant content from PubMed, ensuring unparalleled accuracy and relevance for medical professionals.
                            Its advanced AI technology significantly enhances search precision, making it an indispensable tool for informed medical decisions and research
                        </div> */}
                            </div>
                        </div>

                        <div className={styles.loginpage_content_right}>
                            {screen === 'thanks'
                                && <><img src={SuccessIcon} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                    <span className={styles.thanks_heading}>
                                        Thank you for submitting your interest!
                                    </span>
                                    <span className={styles.thanks_des}>
                                        Gear up, Doctor! Your gateway to groundbreaking research awaits—Tatva-ai credentials landing in your inbox within 48 hours!
                                    </span>

                                    <button
                                        className={`${styles.request_access_button} ${loading ? styles.disabled : ''}`}
                                        onClick={onPressBackToLogin}
                                    >
                                        Back to Login
                                    </button>
                                </>}

                            {screen === 'request_access'
                                && <>
                                    <span className={styles.opps_heading}>
                                        Oops! It looks like you haven't subscribed to Tavta AI yet.
                                    </span>
                                    <span className={styles.opps_des}>
                                        Please click the 'Request Access' button to let us know you're interested.
                                    </span>
                                    <button
                                        className={`${styles.request_access_button} ${loading ? styles.disabled : ''}`}
                                        onClick={() => onPressAccessRequest()}
                                    >
                                        {loading
                                            ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                            : 'Request Access'}
                                    </button>
                                </>}

                            {(screen === 'signin' || screen === 'otp')
                                && <div className={styles.loginpage_content_right_container}>
                                    <span className={styles.loginpage_content_heading}>Login</span>
                                    <span className={styles.loginpage_content_desc}>Please enter the below details</span>
                                    {screen === 'signin'
                                        ? <>
                                            <div className={styles.login_phone_input}>
                                                <span className={styles.login_phone_label}>Enter the mobile number</span>
                                                <ContactInput
                                                    value={phoneNumber}
                                                    inputClass={styles.login_phone_input_field}
                                                    mainContainerClass={styles.phone_desktop_input_container}
                                                    isPhoneIcon={true}
                                                    onChange={handleChange}
                                                    onKeyDown={(event) => handleKeyDown(event, onPressSendOTP)}
                                                    placeholder='Phone...'
                                                />
                                                {/* <span className={styles.login_phone_country_code}>+91</span>
                                                <input
                                                    className={styles.login_phone_input_field}
                                                    type='text'
                                                    value={phoneNumber}
                                                    onChange={handleChange}
                                                    onKeyDown={(event) => handleKeyDown(event, onPressSendOTP)}
                                                    placeholder='Phone...'
                                                /> */}
                                            </div>
                                            {error && <p className={styles.error_message}>{error}</p>}
                                            {!error && <div style={{ marginTop: '30px' }}></div>}
                                            <button
                                                className={`${styles.save_button} ${(phoneNumber.length !== 10 || loading) ? styles.disabled : ''}`}
                                                disabled={(phoneNumber.length !== 10 || loading)}
                                                onClick={onPressSendOTP}
                                            >
                                                {loading
                                                    ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                                    : 'Sign in'}
                                            </button>

                                            {/* <div className={styles.display_text}>
                                        Tatva-ai exclusively offers peer-reviewed, medically relevant content from PubMed, ensuring unparalleled accuracy and relevance for medical professionals.
                                        Its advanced AI technology significantly enhances search precision, making it an indispensable tool for informed medical decisions and research
                                    </div> */}
                                        </>
                                        : <div className={styles.otp_input_wrapper}>
                                            <span className={styles.otp_input_label}>Enter OTP</span>
                                            <OTPInput
                                                value={otp}
                                                onChange={setOtp}
                                                numInputs={6}
                                                inputStyle={styles.otp_input}
                                                renderInput={(props) => <input {...props} />}
                                                containerStyle={styles.otp_input_main}
                                                inputType='number'
                                                shouldAutoFocus
                                            />
                                            {error && <p className={styles.error_message}>{error}</p>}
                                            {!error && <div style={{ marginTop: '20px' }}></div>}
                                            {otpTimer > 0
                                                ? <span className={styles.resend_otp + ' ' + styles.disabled_otp}>Resend OTP ({formatTime(otpTimer)})</span>
                                                : <span className={styles.resend_otp} onClick={onPressResendOTP}>Resend OTP</span>}
                                            <button
                                                className={`${styles.continue_button} ${(otp.length !== 6 || loading) ? styles.disabled : ''}`}
                                                disabled={(otp.length !== 6 || loading)}
                                                onClick={onPressVerifyOTP}
                                                onKeyDown={(event) => { if (event.key === 'Enter') onPressVerifyOTP() }}
                                                ref={otpButtonRef}
                                            >
                                                {loading
                                                    ? <img src={LandingLoader} alt='gif' style={{ height: '80px', marginTop: '-16px' }} />
                                                    : 'Continue'}
                                            </button>
                                        </div>
                                    }
                                </div>}
                        </div>
                    </div>
                }
            </div>
        }
    </>);
};

export default LoginPage;